<template>
	<div class="row">
		<!-- Le document reste sous les yeux pendant la correction : une ligne ne se vérifie qu'en la
		     relisant sur la facture. Il passe au-dessus du tableau sur les écrans étroits, où les deux
		     ne tiennent pas côte à côte. -->
		<div class="col-12 col-xl-5">
			<div class="box invoice-supplier-details__document">
				<iframe v-if="is_pdf" :src="invoice.suppliersocr_pdf_url" class="invoice-supplier-fiche__preview"></iframe>
				<img v-else-if="invoice.suppliersocr_pdf_url" :src="invoice.suppliersocr_pdf_url" class="w-100" />
				<p v-else class="mb-0">{{ $t('invoice.supplier_fiche_no_preview') }}</p>
			</div>
		</div>

		<div class="col-12 col-xl-7">
			<div class="box invoice-supplier-details">
			<LoadingSpinner v-if="!loaded" />

			<template v-else>
				<p v-if="read_only" class="invoice-supplier-details__hint">
					{{ $t('invoice.supplier_details_from_xml') }}
				</p>

				<!-- Pas d'enveloppe table-responsive : le thème y attache sa présentation des listes sur
				     téléphone (cellules en ligne, cases de sélection), et ce tableau a la sienne. Il tient
				     sur un écran large et s'empile sur un écran étroit, sans défilement horizontal. -->
				<table class="table mb-0 invoice-supplier-details__table invoice-supplier-details__lines">
					<!-- Une case modifiable porte son propre libellé : l'en-tête ne sert qu'aux lignes du
					     vendeur, qui s'affichent en texte. -->
					<thead v-if="read_only">
						<tr>
							<th class="text-right">{{ $t('invoice.supplier_details_qty') }}</th>
							<th class="text-right">{{ $t('invoice.supplier_details_part') }}</th>
							<th class="text-right">{{ $t('invoice.supplier_details_unit_price') }}</th>
							<th class="text-right">{{ $t('invoice.supplier_details_discount') }}</th>
							<th class="text-right">{{ $t('invoice.supplier_details_ht') }}</th>
							<th class="text-right">{{ $t('invoice.supplier_details_vat') }}</th>
							<th></th>
						</tr>
					</thead>

					<tbody v-if="!lines.length">
						<tr>
							<td :colspan="columns_count" class="text-center">{{ $t('invoice.supplier_details_empty') }}</td>
						</tr>
					</tbody>

					<!-- Une ligne de facture occupe trois rangées : son libellé sur la largeur des chiffres,
					     parce qu'il est long et que c'est lui qu'on relit sur le document, puis ses
					     chiffres, et enfin le calcul qui les relie. Un tbody par ligne les garde
					     solidaires. -->
					<tbody
						v-for="(line, index) in lines"
						:key="line.isdetails_id || 'new-' + index"
						class="invoice-supplier-details__line"
						:class="{ 'invoice-supplier-details__line--mismatch': hasAmountMismatch(line) }"
					>
						<tr>
							<!-- Le libellé et l'article sous lequel la ligne se range tiennent dans une seule
							     case. Par défaut l'article porte le libellé exact de la ligne ; le chevron
							     accolé ouvre ceux que ce fournisseur a déjà facturés — reprendre l'un d'eux
							     suffit à rapprocher la ligne — ou en nomme un nouveau. Deux factures
							     mensuelles dont le vendeur date le libellé se rangent ainsi sous un même
							     article. Le libellé appartient au vendeur sur une facture électronique : seul
							     l'article s'y choisit alors. -->
							<td :colspan="columns_count - 1" class="invoice-supplier-details__label">
								<b-input-group class="invoice-supplier-details__designation">
									<div class="invoice-supplier-details__field invoice-supplier-details__field--text">
										<span class="invoice-supplier-details__field-label">
											{{ line.article_renamed ? $t('invoice.supplier_details_article_named', { article: line.article_label }) : $t('invoice.supplier_details_label') }}
										</span>
										<b-input
											v-model="line.isdetails_label"
											:readonly="line.locked"
											:state="line.isdetails_label ? null : false"
											@change="onLabelChanged(line)"
										/>
									</div>
									<b-input-group-append>
										<b-dropdown
											right
											variant="outline-secondary"
											:title="$t('invoice.supplier_details_article_choose')"
											:popper-opts="{ positionFixed: true }"
											menu-class="invoice-supplier-details__article-menu"
											@show="article_search = ''"
										>
											<!-- Entrée ne soumet rien : le formulaire n'est là que pour la recherche. -->
											<b-dropdown-form @submit.prevent>
												<b-input v-model="article_search" size="sm" :placeholder="$t('invoice.supplier_details_article_search')" />
											</b-dropdown-form>
											<b-dropdown-item v-if="line.article_renamed" @click="onArticleChosen(line, line.isdetails_label)">
												{{ $t('invoice.supplier_details_article_same') }}
											</b-dropdown-item>
											<b-dropdown-item
												v-for="label in filtered_article_labels"
												:key="label"
												:active="line.article_label === label"
												@click="onArticleChosen(line, label)"
											>
												{{ label }}
												<span v-if="article_hints[label]" class="invoice-supplier-details__option-hint">({{ article_hints[label] }})</span>
											</b-dropdown-item>
											<b-dropdown-item v-if="article_search_label && !article_labels.includes(article_search_label)" @click="onArticleChosen(line, article_search_label)">
												{{ $t('invoice.supplier_details_article_create', { article: article_search_label }) }}
											</b-dropdown-item>
										</b-dropdown>
									</b-input-group-append>
								</b-input-group>
							</td>
							<td class="invoice-supplier-details__label invoice-supplier-details__actions">
								<a v-if="!line.locked" href="#" @click.prevent="removeLine(line)">
									<font-awesome-icon :icon="['fal', 'trash-alt']" />
								</a>
							</td>
						</tr>
						<!-- Chaque case porte son libellé en petit au-dessus de sa valeur : le tableau
						     s'étend sur plusieurs rangées par ligne, et un en-tête unique ne dit plus à
						     quelle case il se rapporte dès qu'on a fait défiler quelques lignes.

						     Les cases suivent l'ordre du calcul qui mène au montant : quantité, quote-part,
						     prix unitaire, remise, puis le montant lui-même et son taux. Toutes restent
						     visibles, la remise et la quote-part comprises : les ouvrir à la demande
						     cachait justement le facteur qui faisait diverger une ligne. -->
						<tr class="invoice-supplier-details__figures">
							<!-- La quantité accepte un prorata, 17/30 : le prix unitaire est alors celui de
							     trente unités, et la quantité en facture dix-sept. Son unité lui est accolée,
							     comme sur le document où elle se lit avec elle. Les listes s'ouvrent en
							     position fixe pour que le défilement du tableau ne les coupe pas. -->
							<td class="text-right">
								<span v-if="line.locked">{{ line.qty_input }} {{ unitLabel(line.isdetails_unit) }}</span>
								<b-input-group v-else class="invoice-supplier-details__group">
									<div class="invoice-supplier-details__field">
										<span class="invoice-supplier-details__field-label">{{ $t('invoice.supplier_details_qty') }}</span>
										<b-input v-select-on-focus :tabindex="index * 4 + 1" v-model="line.qty_input" :state="factorState(line, 'qty')" @blur.native="onFactorLeft(line)" />
									</div>
									<b-input-group-append>
										<b-dropdown size="sm" variant="outline-secondary" right :text="unitLabel(line.isdetails_unit)" :popper-opts="{ positionFixed: true }" menu-class="invoice-supplier-details__unit-menu">
											<b-dropdown-item
												v-for="option in unit_options"
												:key="option.value"
												:active="line.isdetails_unit === option.value"
												@click="setUnit(line, option.value)"
											>
												{{ option.text }}
											</b-dropdown-item>
										</b-dropdown>
									</b-input-group-append>
								</b-input-group>
							</td>
							<td class="text-right">
								<span v-if="line.locked">{{ percentLabel(line.isdetails_part) }}</span>
								<b-input-group v-else size="sm" append="%" class="invoice-supplier-details__group">
									<div class="invoice-supplier-details__field">
										<span class="invoice-supplier-details__field-label">{{ $t('invoice.supplier_details_part') }}</span>
										<b-input v-select-on-focus :tabindex="index * 4 + 2" v-model="line.isdetails_part" :state="factorState(line, 'part')" @blur.native="onPartLeft(line)" />
									</div>
								</b-input-group>
							</td>
							<td class="text-right">
								<span v-if="line.locked">{{ line.isdetails_htunit }}</span>
								<div v-else class="invoice-supplier-details__field">
									<span class="invoice-supplier-details__field-label">{{ $t('invoice.supplier_details_unit_price') }}</span>
									<PriceInput :tabindex="index * 4 + 3" v-model="line.isdetails_htunit" @blur.native="onFactorLeft(line)" />
								</div>
							</td>
							<!-- Une seule case pour les trois formes de remise : la liste accolée dit
							     laquelle s'applique et permet d'en changer, et taper « % », « €/u » ou « € »
							     derrière le nombre fait de même sans quitter le clavier. -->
							<td class="text-right">
								<span v-if="line.locked">{{ discountLabel(line) }}</span>
								<b-input-group v-else class="invoice-supplier-details__group">
									<div class="invoice-supplier-details__field">
										<span class="invoice-supplier-details__field-label">{{ $t('invoice.supplier_details_discount') }}</span>
										<b-input v-select-on-focus :tabindex="index * 4 + 4" v-model="line.isdetails_discount" :state="factorState(line, 'discount')" @blur.native="onDiscountLeft(line)" />
									</div>
									<b-input-group-append>
										<b-dropdown size="sm" variant="outline-secondary" right :text="discountUnit(line.isdetails_discount_type)" :popper-opts="{ positionFixed: true }">
											<b-dropdown-item
												v-for="type in discount_types"
												:key="type"
												:active="line.isdetails_discount_type === type"
												@click="setDiscountType(line, type)"
											>
												{{ discountUnit(type) }} — {{ $t('invoice.supplier_details_discount_types.' + type) }}
											</b-dropdown-item>
										</b-dropdown>
									</b-input-group-append>
								</b-input-group>
							</td>
							<td class="text-right" :title="hasAmountMismatch(line) ? $t('invoice.supplier_details_mismatch') : ''">
								<span v-if="line.locked">{{ line.isdetails_ht }}</span>
								<div v-else class="invoice-supplier-details__field">
									<span class="invoice-supplier-details__field-label">{{ $t('invoice.supplier_details_ht') }}</span>
									<PriceInput v-model="line.isdetails_ht" @change="refreshControl" />
								</div>
							</td>
							<td class="text-right">
								<span v-if="line.locked">{{ percentLabel(line.isdetails_vat_percent) }}</span>
								<b-input-group v-else size="sm" append="%" class="invoice-supplier-details__group">
									<div class="invoice-supplier-details__field">
										<span class="invoice-supplier-details__field-label">{{ $t('invoice.supplier_details_vat') }}</span>
										<b-input v-select-on-focus v-model="line.isdetails_vat_percent" @change="onRateChanged(line)" />
									</div>
								</b-input-group>
							</td>
							<td></td>
						</tr>
						<!-- Le calcul qui mène au montant, écrit en clair dans l'ordre des cases : on y lit
						     d'un coup d'œil lequel des facteurs fait diverger une ligne. -->
						<tr v-if="formulaLabel(line)" class="invoice-supplier-details__formula">
							<td :colspan="columns_count - 1" class="text-right invoice-supplier-details__hint">{{ formulaLabel(line) }}</td>
							<td></td>
						</tr>
					</tbody>
				</table>

				<button v-if="!read_only" class="btn btn-secondary mt-3" @click="addLine">
					<font-awesome-icon :icon="['fal', 'plus']" /> {{ $t('invoice.supplier_details_add') }}
				</button>

				<div v-if="control" class="invoice-supplier-details__control mt-4">
					<h4>{{ $t('invoice.supplier_details_control') }}</h4>

					<table class="table table-sm mb-0 invoice-supplier-details__totals">
						<thead>
							<tr>
								<th></th>
								<th class="text-right">{{ $t('invoice.supplier_details_control_invoice') }}</th>
								<th class="text-right">{{ $t('invoice.supplier_details_control_lines') }}</th>
							</tr>
						</thead>
						<tbody>
							<tr :class="{ 'text-danger': !control.ht }">
								<th>{{ $t('invoice.suppliersocr_ht') }}</th>
								<td class="text-right">{{ amounts.invoice_ht }}</td>
								<td class="text-right">{{ amounts.lines_ht }}</td>
							</tr>
							<tr :class="{ 'text-danger': !control.vat }">
								<th>{{ $t('invoice.suppliersocr_ttc') }}</th>
								<td class="text-right">{{ amounts.invoice_ttc }}</td>
								<td class="text-right">{{ amounts.lines_ttc }}</td>
							</tr>
						</tbody>
					</table>

					<div class="mt-3">
						<ErrorAlert v-if="missing_label" messageI18n="invoice.supplier_details_missing_label" />
						<ErrorAlert v-else-if="invalid_factors" messageI18n="invoice.supplier_details_invalid_factors" />
						<ErrorAlert v-else-if="!balanced" messageI18n="invoice.supplier_details_unbalanced" />

						<button class="btn btn-primary" :disabled="!balanced || missing_label || invalid_factors || saving" @click="saveLines">
							{{ $t('invoice.supplier_details_save') }}
							<font-awesome-icon v-if="saving" :icon="['fal', 'spinner']" spin />
						</button>
					</div>

					<template v-if="rates.length">
						<!-- La référence par taux ne vient que de la lecture optique. Une facture reçue
						     par voie électronique n'en a aucune — sa prédiction se réduit à l'identité du
						     vendeur — et une facture lue peut n'avoir livré aucun taux. Le tableau reste
						     alors utile comme ventilation des lignes, mais il n'y a rien à confronter, et
						     rien à mettre en garde. -->
						<p v-if="has_reference" class="invoice-supplier-details__hint mt-3 mb-1">
							{{ $t('invoice.supplier_details_control_reference_origin') }}
						</p>

						<table class="table table-sm mb-0 mt-3">
							<thead>
								<tr>
									<th>{{ $t('invoice.supplier_details_vat') }}</th>
									<th class="text-right">{{ $t('invoice.supplier_details_control_lines') }}</th>
									<th v-if="has_reference" class="text-right">{{ $t('invoice.supplier_details_control_reference') }}</th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="rate in rates" :key="rate.key" :class="{ 'text-danger': rate.match === false }">
									<td>{{ rate.label }}</td>
									<td class="text-right">{{ rate.lines }}</td>
									<td v-if="has_reference" class="text-right">{{ rate.reference }}</td>
								</tr>
							</tbody>
						</table>
					</template>
				</div>
			</template>
		</div>
		</div>

	</div>
</template>


<script type="text/javascript">
	/* global BigInt */
	// Déclaré ici parce que la configuration ESLint du dépôt ne connaît que l'environnement node, sans
	// les globales d'ES2020. Le navigateur, lui, l'a : la cible de compilation ne descend pas plus bas.

	import Invoice from "@/mixins/Invoice.js"
	import Payment from "@/mixins/Payment.js"

	export default {
		name: "InvoiceSupplierFicheDetails",

		components: {
			ErrorAlert: () => import('GroomyRoot/components/Alert/ErrorAlert'),
			LoadingSpinner: () => import('GroomyRoot/components/Logos/LoadingSpinner_35'),
			PriceInput: () => import('GroomyRoot/components/Inputs/PriceInput')
		},

		mixins: [Invoice, Payment],

		props: {
			invoice: { type: Object, default: () => ({}) },
			invoicesupplier_id: { type: [String, Number], default: null }
		},

		data() {
			return {
				loaded: false,
				saving: false,
				lines: [],
				// Articles déjà facturés par ce fournisseur, proposés sur le libellé des lignes.
				articles: [],
				// Recherche dans la liste des articles. Une seule liste s'ouvre à la fois : elle est
				// partagée, et remise à blanc à chaque ouverture.
				article_search: '',
				control: null,
				// Montants du contrôle, formatés dans une méthode : priceFormat attend la devise, et un
				// computed rendrait des promesses.
				amounts: {
					lines_ht: '',
					invoice_ht: '',
					lines_ttc: '',
					invoice_ttc: ''
				},
				rates: []
			}
		},

		computed: {
			/**
			 * Dit si la ventilation par taux a quelque chose à quoi se confronter.
			 *
			 * La référence ne vient que de la lecture optique : une facture reçue par voie électronique
			 * n'en a pas, et une facture lue n'en a pas toujours. Sans elle, le tableau des taux n'est
			 * qu'une ventilation des lignes — l'annoncer comme une comparaison serait mentir.
			 */
			has_reference() {
				return this.rates.some(rate => rate.reference !== '')
			},

			/**
			 * Dit si une ligne attend encore son libellé.
			 *
			 * Le serveur l'exige, et il refuse l'envoi entier : sans ce contrôle, ajouter une ligne puis
			 * oublier de la nommer ferait échouer le dépôt de toutes les autres, avec un message qui ne
			 * dirait pas laquelle est en cause.
			 */
			missing_label() {
				return this.lines.some(line => !line.isdetails_label)
			},

			/**
			 * Dit si le détail peut être déposé.
			 *
			 * Les deux totaux doivent retomber sur ceux de la facture : le hors taxes, et la taxe — donc
			 * le toutes taxes, qui en est la somme. Tant que l'un des deux diverge, les lignes racontent
			 * autre chose que la facture, et les enregistrer ferait porter cette contradiction à la
			 * ventilation comptable puis à l'export.
			 */
			balanced() {
				// Sur une facture dont le vendeur a déclaré toutes les lignes, l'enregistrement ne touche
				// ni aux montants ni aux quantités : il ne porte que le nom de l'article, qui est à nous.
				// L'équilibre des totaux n'a alors rien à autoriser.
				if(this.read_only) return true

				return !!this.control && this.control.ht && this.control.vat
			},

			/**
			 * Nombre de colonnes du tableau — quantité et son unité, quote-part, prix unitaire, remise,
			 * hors taxes, taux et la colonne de la suppression, qui se pose à droite du libellé — dont
			 * dépendent les rangées pleine largeur et le message d'absence de ligne.
			 */
			columns_count() {
				return 7
			},

			/**
			 * Formes de remise proposées : pourcentage du prix unitaire, montant retranché de chaque unité,
			 * montant retranché une seule fois de la ligne.
			 */
			discount_types() {
				return ['percent', 'amount', 'line']
			},

			/**
			 * Devise du document. La fiche la donne toujours ; la valeur par défaut de la prop, elle, ne la
			 * porte pas.
			 */
			currency() {
				return this.invoice.suppliersocr_currency || 'EUR'
			},

			/**
			 * Symbole de la devise du document, qui désigne une remise en montant.
			 */
			currency_symbol() {
				return this.currencySymbol(this.currency)
			},

			/**
			 * Recherche saisie dans la liste des articles, débarrassée de ses espaces.
			 */
			article_search_label() {
				return this.article_search.trim()
			},

			/**
			 * Articles du fournisseur dont le libellé contient la recherche, sans tenir compte de la casse
			 * ni des accents.
			 *
			 * Calculés une fois pour toutes les lignes : chaque ligne a sa liste, mais une seule s'ouvre à
			 * la fois et toutes partagent la recherche.
			 */
			filtered_article_labels() {
				const needle = this.accentsTidy(this.article_search_label)

				if(!needle) return this.article_labels

				return this.article_labels.filter(label => this.accentsTidy(label).includes(needle))
			},

			/**
			 * Dit si une case de facteur est illisible ou hors de ses bornes.
			 *
			 * Le serveur refuse alors l'envoi entier, avec un message qui ne dirait pas quelle ligne est en
			 * cause : comme pour le libellé, l'enregistrement reste fermé tant qu'une case est en rouge.
			 */
			invalid_factors() {
				return this.lines.some(line => !line.locked && ['qty', 'part', 'discount'].some(factor => this.factorState(line, factor) === false))
			},

			/**
			 * Le document reçu est tantôt un PDF, tantôt une image selon la source : le premier
			 * s'affiche dans un cadre, la seconde directement.
			 */
			is_pdf() {
				return !!this.invoice.suppliersocr_pdf_url && this.invoice.suppliersocr_pdf_url.includes('.pdf')
			},

			/**
			 * Le détail ne se corrige pas quand le vendeur l'a déclaré lui-même.
			 *
			 * C'est l'origine des lignes qui le dit, et non la provenance de la facture : un dépôt
			 * manuel rattaché après coup à son document électronique en porte les lignes, et elles font
			 * foi tout autant.
			 */
			read_only() {
				return this.lines.some(line => line.locked)
			},

			/**
			 * Articles déjà facturés par ce fournisseur, tels que la liste les propose.
			 *
			 * Ce sont leurs libellés seuls, et non les articles eux-mêmes : c'est le libellé que la ligne
			 * porte et sur lequel le serveur retrouve l'article, et le champ reste ouvert à un nom
			 * qu'aucun d'eux ne porte encore. Le serveur les rend déjà rangés.
			 */
			article_labels() {
				return this.articles.map(article => article.isarticles_label)
			},

			/**
			 * Précision affichée à côté de chaque suggestion : le code de l'article chez le vendeur, son
			 * unité, ou rien quand il n'en porte pas.
			 *
			 * Rangée par libellé une fois pour toutes plutôt que retrouvée à chaque suggestion affichée :
			 * la liste se rouvre à chaque ligne du tableau, et elle ne change qu'avec les articles.
			 */
			article_hints() {
				return Object.fromEntries(this.articles.map(article => [
					article.isarticles_label,
					[article.isarticles_code, this.unitLabel(article.isarticles_unit)].filter(part => part).join(' — ')
				]))
			},

			/**
			 * Unités proposées à la saisie, triées sur leur libellé.
			 *
			 * Les codes sont ceux d'UN/ECE Rec 20, le vocabulaire qu'une facture électronique déclare et
			 * que le serveur attend. La liste reprend celle de sa table de correspondance : un code
			 * qu'elle ne connaîtrait pas serait écarté à l'enregistrement, et l'unité disparaîtrait sans
			 * rien dire. Elle est énumérée ici plutôt que lue dans les traductions, dont le $t ne rend
			 * que des chaînes.
			 *
			 * Aucune entrée vide : une ligne modifiable porte toujours une unité, l'unité neutre à
			 * défaut d'une autre. Se remettre sur du vide ne mènerait qu'à la retrouver au rechargement.
			 */
			unit_options() {
				const codes = ['C62', 'KGM', 'GRM', 'TNE', 'LTR', 'MLT', 'MTK', 'MTQ', 'MTR', 'DAY', 'MON', 'HUR', 'KWH', 'SET', 'PR', 'BX', 'BG', 'CT', 'PF']
				const options = codes.map(code => ({ value: code, text: this.unitLabel(code) }))

				options.sort((a, b) => a.text.localeCompare(b.text))

				return options
			}
		},

		methods: {
			/**
			 * Charge les articles du fournisseur, les lignes de la facture, puis son contrôle.
			 *
			 * Les articles viennent en premier : ce sont eux que la saisie des libellés propose.
			 */
			async init_component() {
				this.loaded = false

				this.articles = await this.getInvoiceSupplierArticles(this.invoicesupplier_id) || []

				const lines = await this.getInvoiceSupplierDetails(this.invoicesupplier_id) || []
				this.lines = lines.map(line => this.prepareLine(line))

				await this.refreshControl()

				this.loaded = true
			},

			/**
			 * Met une ligne en état d'être éditée, et retient si elle est verrouillée.
			 */
			prepareLine(line) {
				const locked = line.isdetails_origin === 'xml'

				// Une ligne modifiable dont la quantité n'a pas été lue part sur une unité, comme elle part
				// sur l'unité neutre : c'est le cas de loin le plus courant, et l'utilisateur la corrige au
				// moment où il recontrôle la ligne. Rien n'est écrit en base avant son enregistrement.
				// La base rend un décimal à cinq chiffres : sans cela le champ afficherait 2.00000.
				const qte = this.trimDecimals(line.isdetails_qte)
				const base = this.trimDecimals(line.isdetails_base_qty)
				let qty_input = qte === null && !locked ? '1' : qte

				if(qty_input !== null && base !== null && base !== '1') {
					qty_input += '/' + base
				}

				const prepared = {
					...line,
					isdetails_rebillable: !!line.isdetails_rebillable,
					// Une ligne qui n'annonce aucune unité part sur l'unité neutre. Le serveur écarte ce
					// que la lecture optique lui donne et qu'il ne reconnaît pas, si bien qu'une unité
					// absente ne se distingue pas d'une unité mal lue : une case vide n'apprend rien à qui
					// recontrôle la facture, et c'est le moment où il la corrige. Celle du vendeur est
					// laissée telle quelle — son document la déclare, elle ne se déduit pas ici.
					isdetails_unit: locked ? line.isdetails_unit : line.isdetails_unit || 'C62',
					// L'écran corrige la ligne en regard du PDF : le montant s'y lit donc dans la devise du
					// document, là où la clé ordinaire porte sa contrepartie comptable. Hors devise
					// étrangère, les deux se confondent et rien ne change.
					isdetails_ht: line.isdetails_ht_src === null ? line.isdetails_ht : line.isdetails_ht_src,
					// Quantité et quantité de base se saisissent ensemble, sous la forme d'un prorata dès
					// que la seconde n'est pas l'unité.
					qty_input,
					isdetails_htunit: this.trimDecimals(line.isdetails_htunit),
					isdetails_discount: this.trimDecimals(line.isdetails_discount),
					// Une ligne sans remise propose le pourcentage, la forme la plus courante.
					isdetails_discount_type: line.isdetails_discount_type || 'percent',
					// Une ligne modifiable sans quote-part relevée est facturée en entier : la case part sur
					// cent pour cent, valeur que l'enregistrement dépose telle quelle.
					isdetails_part: line.isdetails_part === null && !locked ? '100' : this.trimDecimals(line.isdetails_part),
					article_label: line.isdetails_article ? line.isdetails_article.isarticles_label : line.isdetails_label,
					article_renamed: !!line.isdetails_article && line.isdetails_article.isarticles_label !== line.isdetails_label,
					locked
				}

				return prepared
			},

			/**
			 * Ajoute une ligne vide au tableau. Elle n'existe qu'à l'écran jusqu'au dépôt de l'ensemble.
			 */
			addLine() {
				this.lines.push(this.prepareLine({
					isdetails_id: null,
					isdetails_label: '',
					isdetails_qte: null,
					isdetails_base_qty: null,
					isdetails_unit: null,
					isdetails_htunit: null,
					isdetails_discount: null,
					isdetails_discount_type: null,
					isdetails_part: null,
					isdetails_ht: 0,
					isdetails_vat_percent: null,
					isdetails_start_date: null,
					isdetails_end_date: null,
					isdetails_horse: null,
					isdetails_rebillable: false,
					isdetails_origin: 'manual'
				}))
			},

			/**
			 * Ce que la ligne enverra au serveur, dans un ordre stable.
			 */
			linePayload(line) {
				const quantity = this.splitQuantity(line.qty_input)
				const discount = this.toNumberOrNull(line.isdetails_discount)

				return {
					isdetails_id: line.isdetails_id,
					isdetails_label: line.isdetails_label,
					isdetails_article_label: line.article_label,
					isdetails_qte: this.toNumberOrNull(quantity.qte),
					// Une base illisible ou nulle ne divise rien : le prix s'entend alors pour une unité.
					isdetails_base_qty: this.toNumberOrNull(quantity.base) || 1,
					isdetails_unit: line.isdetails_unit,
					isdetails_htunit: this.toNumberOrNull(line.isdetails_htunit),
					isdetails_discount: discount,
					isdetails_discount_type: discount === null ? null : line.isdetails_discount_type,
					isdetails_part: this.toNumberOrNull(line.isdetails_part),
					isdetails_ht: this.toNumberOrNull(line.isdetails_ht) || 0,
					isdetails_vat_percent: this.toNumberOrNull(line.isdetails_vat_percent),
					// La période, le cheval et la refacturation ne se corrigent pas ici — ils tiendront
					// dans leur propre onglet, une fois la facture recontrôlée. Ils sont malgré tout
					// renvoyés tels qu'ils sont arrivés : le serveur réécrit ces colonnes à chaque dépôt,
					// et les taire reviendrait à les vider.
					isdetails_start_date: line.isdetails_start_date,
					isdetails_end_date: line.isdetails_end_date,
					isdetails_horse: line.isdetails_horse ? line.isdetails_horse.horse_id : null,
					isdetails_rebillable: line.isdetails_rebillable
				}
			},

			/**
			 * Dépose l'ensemble des lignes.
			 *
			 * Le bouton ne s'ouvre que lorsque les totaux retombent sur ceux de la facture : déposer un
			 * détail qui ne se réconcilie pas reviendrait à installer en base une incohérence que la
			 * ventilation comptable et l'export auraient ensuite à porter. L'écran travaille donc en
			 * local jusque-là, et n'écrit qu'une fois.
			 */
			async saveLines() {
				this.saving = true

				const saved = await this.saveInvoiceSupplierDetails(
					this.invoicesupplier_id,
					this.lines.map(line => this.linePayload(line))
				)

				this.saving = false

				if(!saved) {
					this.failureToast("toast.info_save_failed")
					return
				}

				this.lines = saved.map(line => this.prepareLine(line))

				await this.refreshControl()

				this.successToast("toast.info_modif_succes")
				this.$emit('invoice-updated')
			},

			/**
			 * Le nom d'article vient d'être choisi dans la liste ou créé : il cesse de suivre le libellé
			 * de la ligne.
			 *
			 * Choisir un article existant et en nommer un nouveau passent par le même chemin : le nom tapé
			 * dans la recherche devient celui de l'article, comme s'il avait été pris parmi les autres.
			 *
			 * Repasser le nom d'article à celui de la ligne les réunit à nouveau, plutôt que de figer un
			 * lien rompu sur une égalité retrouvée par hasard.
			 */
			onArticleChosen(line, label) {
				line.article_label = label
				line.article_renamed = label !== line.isdetails_label
			},

			/**
			 * Le nom d'article suit le libellé de la ligne tant qu'il n'en a pas été distingué.
			 *
			 * C'est le comportement attendu par défaut : on corrige un libellé mal lu, et l'article se
			 * range sous ce nom. Dès que l'utilisateur a nommé l'article autrement — pour rapprocher
			 * deux factures mensuelles dont le vendeur date le libellé, par exemple — ce nom lui
			 * appartient, et corriger la ligne ne le défait plus.
			 */
			onLabelChanged(line) {
				if(!line.article_renamed) {
					line.article_label = line.isdetails_label
				}
			},

			/**
			 * Recalcule le montant hors taxes en quittant l'un de ses facteurs : quantité, quote-part, prix
			 * unitaire ou remise.
			 *
			 * Le déclencheur est la sortie du champ, et non sa modification : la lecture optique se
			 * trompe sur une partie des lignes, et le geste de contrôle consiste justement à parcourir
			 * les facteurs au clavier. Exiger une réécriture laisserait en place un montant faux dont les
			 * facteurs sont pourtant justes.
			 *
			 * Un montant qui retombe déjà sur ses facteurs à moins d'un centime est laissé tel quel : c'est
			 * celui du document, et les vendeurs n'arrondissent pas tous pareil — Groomy tronque une pension
			 * à 82,875 en 82,87 là où l'arrondi donnerait 82,88. Le réécrire déséquilibrerait la facture
			 * d'un centime.
			 */
			onFactorLeft(line) {
				const expected = this.expectedCents(line)
				const valid = ['qty', 'part', 'discount'].every(factor => this.factorState(line, factor) !== false)

				// Une case en défaut ne réécrit rien : le montant tiré d'une faute de frappe effacerait
				// celui du document.
				if(expected !== null && valid && !this.isWithinACent(line.isdetails_ht, expected)) {
					line.isdetails_ht = (Number(this.roundCents(expected)) / 100).toFixed(2)
				}

				this.refreshControl()
			},

			/**
			 * Lit la forme de la remise sur ce qui suit le nombre : « 10 % », « 5 €/u » ou « 5 € » la fixent
			 * sans passer par la liste, puis le montant de la ligne est repris. La devise s'écrit par son
			 * symbole ou par son code (« 5 EUR/u »), sans égard à la casse ; une saisie que rien ne
			 * reconnaît reste telle quelle, et sa case passe en rouge.
			 */
			onDiscountLeft(line) {
				if(line.isdetails_discount !== null) {
					let text = String(line.isdetails_discount).trim()
					let type = null

					if(text.endsWith('%')) {
						type = 'percent'
						text = text.slice(0, -1)
					}
					else {
						const per_unit = /\/u$/i.test(text)

						if(per_unit) text = text.slice(0, -2).trim()

						const sign = [this.currency_symbol, this.currency].find(candidate => text.toUpperCase().endsWith(candidate.toUpperCase()))

						if(sign) text = text.slice(0, -sign.length)
						if(sign || per_unit) type = per_unit ? 'amount' : 'line'
					}

					if(type) {
						line.isdetails_discount_type = type
						line.isdetails_discount = text.trim()
					}
				}

				this.onFactorLeft(line)
			},

			/**
			 * La quote-part se saisit volontiers suivie de son signe, « 50 % » : il est retiré, la case
			 * l'affiche déjà à côté.
			 */
			onPartLeft(line) {
				line.isdetails_part = this.withoutPercent(line.isdetails_part)

				this.onFactorLeft(line)
			},

			/**
			 * Le taux se saisit volontiers suivi de son signe, « 20 % » : le signe est retiré, la case
			 * l'affiche déjà à côté.
			 */
			onRateChanged(line) {
				line.isdetails_vat_percent = this.withoutPercent(line.isdetails_vat_percent)

				this.refreshControl()
			},

			/**
			 * Une saisie de pourcentage débarrassée de son signe.
			 */
			withoutPercent(value) {
				if(value === null) return null

				return String(value).replace('%', '').trim()
			},

			/**
			 * Change l'unité d'une ligne. Elle n'entre pas dans le montant, mais le contrôle se rejoue à
			 * chaque geste.
			 */
			setUnit(line, unit) {
				line.isdetails_unit = unit

				this.refreshControl()
			},

			/**
			 * Change la forme de la remise d'une ligne, puis reprend son montant.
			 */
			setDiscountType(line, type) {
				line.isdetails_discount_type = type

				this.onFactorLeft(line)
			},

			/**
			 * Signe d'une forme de remise : %, montant par unité ou montant sur la ligne.
			 */
			discountUnit(type) {
				if(type === 'amount') return this.currency_symbol + '/u'
				if(type === 'line') return this.currency_symbol

				return '%'
			},

			/**
			 * Remise d'une ligne suivie de sa forme, pour l'affichage en lecture seule et dans le calcul.
			 */
			discountLabel(line) {
				if(line.isdetails_discount === null) return ''

				return line.isdetails_discount + ' ' + this.discountUnit(line.isdetails_discount_type)
			},

			/**
			 * Pourcentage suivi de son signe — quote-part ou taux —, vide quand il n'est pas renseigné.
			 */
			percentLabel(value) {
				if(value === null) return ''

				return value + ' %'
			},

			/**
			 * État d'une case de facteur : rouge si elle est illisible ou hors de ses bornes, neutre sinon.
			 *
			 * Une case vide est neutre : le facteur n'a pas été lu, la ligne est incomplète. Les bornes sont
			 * celles du serveur — quatre décimales au plus, quote-part entre 0 et 100, remise positive et
			 * jamais au-delà de 100 % —, auxquelles s'ajoute une remise qui ferait changer de signe le
			 * montant de la ligne : c'est presque toujours une faute de frappe.
			 *
			 * @param {Object} line
			 * @param {String} factor 'qty', 'part' ou 'discount'.
			 * @return {Boolean|null} false si la case est en défaut, null sinon.
			 */
			factorState(line, factor) {
				if(factor === 'qty') {
					const text = line.qty_input === null ? '' : String(line.qty_input).trim()

					if(text === '') return null

					const parts = text.split('/')
					const qte = this.toRational(parts[0])
					const base = parts.length === 2 ? this.toRational(parts[1]) : { num: BigInt(1) }

					return parts.length > 2 || qte === null || base === null || base.num <= BigInt(0) ? false : null
				}

				const value = factor === 'part' ? line.isdetails_part : line.isdetails_discount

				if(value === null || String(value).trim() === '') return null

				const decimal = this.parseDecimal(value)

				if(decimal === null || decimal.scale > 4 || decimal.value < BigInt(0)) return false

				const rational = this.toRational(value)

				if(factor === 'part') {
					return rational.num === BigInt(0) || rational.num > BigInt(100) * rational.den ? false : null
				}

				if(line.isdetails_discount_type === 'percent' && rational.num > BigInt(100) * rational.den) return false

				const before = this.expectedCents({ ...line, isdetails_discount: null })
				const after = this.expectedCents(line)

				if(before === null || after === null) return null

				return before.num !== BigInt(0) && after.num !== BigInt(0) && (before.num < BigInt(0)) !== (after.num < BigInt(0)) ? false : null
			},

			/**
			 * Calcul qui mène au montant d'une ligne, écrit en clair dans l'ordre des cases :
			 * 17/30 × 50 % × (325 − 10 %) = 82,875, ou 2 × (100 − 5 €/u) − 10 € = 180.
			 *
			 * Les facteurs sont repris tels qu'ils sont saisis. Une quote-part pleine est tue : elle ne
			 * change rien et chargerait chaque ligne. Rien n'est rendu tant qu'un facteur manque.
			 */
			formulaLabel(line) {
				const expected = this.expectedCents(line)

				if(expected === null) return ''

				const discounted = this.toRational(line.isdetails_discount) !== null
				const part = this.toNumberOrNull(line.isdetails_part)
				let formula = line.qty_input

				if(part !== null && part !== 100) {
					formula += ' × ' + this.percentLabel(line.isdetails_part)
				}

				if(discounted && line.isdetails_discount_type !== 'line') {
					formula += ' × (' + line.isdetails_htunit + ' − ' + this.discountLabel(line) + ')'
				}
				else {
					formula += ' × ' + line.isdetails_htunit
				}

				if(discounted && line.isdetails_discount_type === 'line') {
					formula += ' − ' + this.discountLabel(line)
				}

				// Le résultat garde ses décimales au-delà du centime : un 82,875 que le vendeur a tronqué en
				// 82,87 explique de lui-même l'écart, là où un arrondi à 82,88 le ferait passer pour une erreur.
				const amount = Number(expected.num) / Number(expected.den) / 100

				const locale = this.$i18n.locale() === 'code' ? 'fr' : this.$i18n.locale()

				return formula + ' = ' + amount.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 4 })
			},

			/**
			 * Sépare une quantité saisie en prorata — 17/30 — en quantité et quantité de base. Sans
			 * barre, la base est l'unité.
			 *
			 * Les deux restent des chaînes : le calcul du montant les relit telles qu'elles sont écrites.
			 *
			 * @return {Object} {qte: String|null, base: String}
			 */
			splitQuantity(text) {
				if(text === null || text === undefined) return { qte: null, base: '1' }

				const parts = String(text).split('/')

				return { qte: parts[0].trim(), base: parts.length > 1 ? parts[1].trim() : '1' }
			},

			/**
			 * Lit un nombre écrit en chiffres, avec une virgule ou un point décimal, et le rend sous forme
			 * d'entier accompagné du nombre de décimales qu'il portait.
			 *
			 * @return {Object|null} {value: BigInt, scale: Number}, ou null si ce n'est pas un nombre.
			 */
			parseDecimal(value) {
				if(value === null || value === undefined || value === '') return null

				const text = String(value).trim().replace(',', '.')

				if(!/^-?\d*\.?\d*$/.test(text) || !/\d/.test(text)) return null

				const negative = text.charAt(0) === '-'
				const parts = text.replace('-', '').split('.')
				const whole = parts[0] || '0'
				const fraction = parts[1] || ''
				const digits = BigInt(whole + fraction)

				return { value: negative ? -digits : digits, scale: fraction.length }
			},

			/**
			 * Dit si le montant d'une ligne contredit ses propres facteurs.
			 *
			 * C'est le signe le plus sûr d'une lecture optique fautive : sur les factures déjà lues,
			 * quantité fois prix unitaire ne retombe pas sur le total relevé une fois sur deux. Un écart de
			 * moins d'un centime n'en est pas un : il ne vient que de la façon dont le vendeur arrondit.
			 *
			 * Rien n'est signalé quand l'un des facteurs manque : une ligne dont le prix unitaire n'a pas
			 * été lu n'est pas incohérente, elle est incomplète.
			 */
			hasAmountMismatch(line) {
				const expected = this.expectedCents(line)

				if(expected === null || this.parseDecimal(line.isdetails_ht) === null) return false

				return !this.isWithinACent(line.isdetails_ht, expected)
			},

			/**
			 * Montant hors taxes qu'annoncent les facteurs d'une ligne, en centimes, sous forme de fraction
			 * exacte.
			 *
			 * Quantité × quote-part × (prix unitaire − remise par unité) − remise sur la ligne : le prix
			 * unitaire est remisé — en pourcentage, ou d'un montant par unité —, rapporté à la quantité de
			 * base, multiplié par la quantité puis réduit à la quote-part facturée. Une remise sur la ligne
			 * se retranche en dernier, une seule fois, du montant ainsi obtenu.
			 *
			 * Tout reste en entiers exacts jusqu'au bout, numérateur et dénominateur séparés : en virgule
			 * flottante, 3 × 0,1 vaut 0,30000000000000004, et un prorata de 17/30 n'a pas d'écriture décimale
			 * finie. L'arrondi, s'il y en a un, se fait une seule fois, par qui reçoit la fraction.
			 *
			 * @return {Object|null} {num: BigInt, den: BigInt} avec den > 0, ou null s'il manque un facteur.
			 */
			expectedCents(line) {
				const quantity = this.splitQuantity(line.qty_input)
				const qte = this.toRational(quantity.qte)
				const base = this.toRational(quantity.base)
				let price = this.toRational(line.isdetails_htunit)

				if(qte === null || base === null || price === null || base.num <= BigInt(0)) return null

				const discount = this.toRational(line.isdetails_discount)

				if(discount !== null && line.isdetails_discount_type === 'amount') {
					price = { num: price.num * discount.den - discount.num * price.den, den: price.den * discount.den }
				}
				else if(discount !== null && line.isdetails_discount_type === 'percent') {
					price = { num: price.num * (BigInt(100) * discount.den - discount.num), den: price.den * discount.den * BigInt(100) }
				}

				let num = qte.num * price.num * base.den * BigInt(100)
				let den = qte.den * price.den * base.num

				const part = this.toRational(line.isdetails_part)

				if(part !== null) {
					num = num * part.num
					den = den * part.den * BigInt(100)
				}

				if(discount !== null && line.isdetails_discount_type === 'line') {
					num = num * discount.den - discount.num * BigInt(100) * den
					den = den * discount.den
				}

				return { num, den }
			},

			/**
			 * Dit si un montant saisi retombe sur un montant attendu à moins d'un centime près.
			 *
			 * @param {String|Number} amount Montant en unités monétaires, tel qu'il est écrit.
			 * @param {Object} expected Fraction de centimes rendue par expectedCents.
			 */
			isWithinACent(amount, expected) {
				const written = this.toRational(amount)

				if(written === null) return false

				// Les deux fractions sont ramenées au même dénominateur : l'écart reste entier, et se
				// compare au centime sans division.
				const gap = written.num * BigInt(100) * expected.den - expected.num * written.den
				const cent = written.den * expected.den

				return gap < cent && -gap < cent
			},

			/**
			 * Arrondit une fraction de centimes au centime, au demi supérieur en valeur absolue — le signe
			 * est mis de côté pour qu'un avoir s'arrondisse comme une facture.
			 *
			 * @return {BigInt}
			 */
			roundCents(fraction) {
				const negative = fraction.num < BigInt(0)
				const absolute = negative ? -fraction.num : fraction.num
				const quotient = absolute / fraction.den
				const remainder = absolute % fraction.den
				const rounded = remainder * BigInt(2) >= fraction.den ? quotient + BigInt(1) : quotient

				return negative ? -rounded : rounded
			},

			/**
			 * Nombre écrit, sous forme de fraction exacte.
			 *
			 * @return {Object|null} {num: BigInt, den: BigInt}, ou null si ce n'est pas un nombre.
			 */
			toRational(value) {
				const decimal = this.parseDecimal(value)

				if(decimal === null) return null

				return { num: decimal.value, den: this.powerOfTen(decimal.scale) }
			},

			/**
			 * Puissance de dix, en entier exact.
			 *
			 * Écrite plutôt que calculée : l'opérateur d'exponentiation est réécrit en Math.pow à la
			 * compilation, et Math.pow refuse les entiers exacts.
			 *
			 * @param {Number} exponent
			 * @return {BigInt}
			 */
			powerOfTen(exponent) {
				return BigInt('1' + '0'.repeat(exponent))
			},

			/**
			 * Retire les zéros décimaux que la base ajoute à un décimal.
			 *
			 * @param {String|Number|null} value
			 * @return {String|null}
			 */
			trimDecimals(value) {
				if(value === null || value === undefined || value === '') return value

				const parsed = parseFloat(value)

				return isNaN(parsed) ? value : String(parsed)
			},

			/**
			 * Nombre attendu par le serveur, à partir d'une saisie qui peut porter une virgule décimale
			 * ou être vide.
			 *
			 * Il est lu comme le lit le calcul du montant : une saisie que celui-ci ignore — « 5 $ »,
			 * « 50 % » — ne doit pas partir en base sous une autre valeur que celle qu'il a comptée.
			 */
			toNumberOrNull(value) {
				const rational = this.toRational(value)

				if(rational === null) return null

				return Number(rational.num) / Number(rational.den)
			},

			/**
			 * Retire une ligne du tableau. Rien n'est perdu tant que l'ensemble n'a pas été déposé :
			 * quitter l'onglet sans enregistrer la ramène.
			 */
			removeLine(line) {
				this.lines = this.lines.filter(other => other !== line)

				this.refreshControl()
			},

			/**
			 * Reprend le contrôle des sommes sur l'état de l'écran, et prépare son affichage.
			 *
			 * Il est rejoué à chaque geste : c'est lui qui ouvre ou ferme l'enregistrement, il ne peut
			 * donc pas porter sur ce qui est en base pendant que l'utilisateur corrige.
			 */
			async refreshControl() {
				this.control = await this.checkInvoiceSupplierDetailsSum(
					this.invoicesupplier_id,
					this.lines.map(line => this.linePayload(line))
				)

				if(!this.control) return

				const currency = this.currency

				this.amounts = {
					lines_ht: await this.priceFormat(this.control.lines_ht, currency, true),
					invoice_ht: await this.priceFormat(this.control.invoice_ht, currency, true),
					lines_ttc: await this.priceFormat(this.control.lines_ttc, currency, true),
					invoice_ttc: await this.priceFormat(this.control.invoice_ttc, currency, true)
				}

				const rates = []

				for(const rate of this.control.rates) {
					// La référence se lit sur la base hors taxes quand le document l'a donnée, et sur le
					// montant de taxe sinon : c'est ce que le serveur a comparé.
					const on_ht = rate.reference_ht !== null
					const reference = on_ht ? rate.reference_ht : rate.reference_vat

					rates.push({
						key: rate.rate === null ? 'null' : rate.rate,
						label: rate.rate === null ? this.$t('invoice.supplier_details_control_no_rate') : this.percentLabel(rate.rate),
						lines: await this.priceFormat(on_ht ? rate.lines_ht : rate.lines_vat, currency, true),
						reference: reference === null ? '' : await this.priceFormat(reference, currency, true),
						match: rate.match
					})
				}

				this.rates = rates
			},

			/**
			 * Libellé d'un code d'unité.
			 *
			 * Le document d'un vendeur déclare son unité dans toute l'étendue de la norme Rec 20, dont
			 * nos libellés ne couvrent que les codes rencontrés jusqu'ici. Un code inconnu est rendu tel
			 * quel : il reste lisible, là où la clé de traduction non résolue s'afficherait brute.
			 */
			unitLabel(code) {
				if(!code) return ''

				const key = 'invoice.supplier_details_units.' + code
				const label = this.$t(key)

				return label === key ? code : label
			}
		},

		mounted() {
			this.init_component()
		}
	}
</script>
